import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GetModuleInstancePhases, ClearModuleInstancePhases, ModuleInstancePhasesSubscribe } from '../../../../store/ModuleInstancePhaseAction';
import * as Helper from '../../../../util/Helper';
import * as InstancePhaseStatus from './InstancePhaseStatus';
import { Tooltip } from 'reactstrap';
import Loading from '../../../misc/Loading';
import StatusCode from '../../../../util/StatusCode';
import { Link } from 'react-router-dom'
import { CoreModuleInstancePhase, ModuleInstanceStatus } from '../../../../models/CoreModels';
import { RequestState } from '../../../../models/RequestState';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GeneralPhaseNames from '../../../../models/GeneralPhaseNames';
import { RootState } from '../../../../store/store';
import { ModulePhaseStatusSeverityIcon } from '../../../misc/ModulePhaseStatusSeverityIcon';


interface InstancePhaseProps {
    moduleId: string
    moduleInstanceId: string
    completed: boolean
}


const InstancePhase: React.FC<InstancePhaseProps> = (props) => {
    const dispatch = useDispatch();
    const instancePhases = useSelector<RootState, RequestState<CoreModuleInstancePhase[]>>(state => state.instancePhase.instancePhases);
    const [isOpen, setIsOpen] = useState<{ [key: string]: boolean }>({});
    const [useAnimation, setUseAnimation] = useState(false);

    const { t } = useTranslation('configuration');

    useEffect(() => {
        if (props.moduleInstanceId) {
            GetModuleInstancePhases(props.moduleInstanceId)(dispatch);
        }
    }, [dispatch, props.moduleInstanceId]);

    useEffect(() => {
        if (props.completed) {
            setUseAnimation(false);
        } else {
            setUseAnimation(true);
            ClearModuleInstancePhases()(dispatch);
            return ModuleInstancePhasesSubscribe(props.moduleInstanceId)(dispatch);
        }
    }, [dispatch, props.completed, props.moduleInstanceId]);

    const getPhaseIcon = (phase: CoreModuleInstancePhase) => {
        switch (phase.phaseName) {
            case GeneralPhaseNames.Start:
                return <span className="icon"> <FontAwesomeIcon icon="circle-play" className="icon_ok" /></span>;
            case GeneralPhaseNames.End:
                if (phase.status !== ModuleInstanceStatus.Completed) {
                    return <span className="icon"> <FontAwesomeIcon icon="ban" className="icon_critical" /></span>;
                } else {
                    return <span className="icon"> <FontAwesomeIcon icon="bullseye" className="icon_ok" /></span>;
                }
            default:
                return <ModulePhaseStatusSeverityIcon
                    status={phase.status}
                    severity={phase.severity} />;
        }
    }

    const renderPhases = (phases: CoreModuleInstancePhase[]) => {
        return <><div className="instance-phase-chart-container">
            {phases.map(ip => (
                <div key={ip.id} className={"instance-phase-chart" + (useAnimation ? " animation" : '')} id={"Tooltip-" + ip.id}>
                    <div>
                        {getPhaseIcon(ip)}
                    </div>
                    <span className="phase-title">{ip.phaseName}</span>
                    <Tooltip placement="bottom-start" isOpen={isOpen[ip.id]} target={"Tooltip-" + ip.id} toggle={() => setIsOpen({ [ip.id]: !isOpen[ip.id] })}>
                        <p><b>{ip.phaseName}</b></p>
                        <span><span className="title">{t('instancePhasePreview.started')}</span> {Helper.formatDate(ip.startedAt)}</span>
                        <span><span className="title">{t('instancePhasePreview.status')}</span> {t(`moduleInstancePhaseStatus.${ip.status}`)}</span>
                        <span><span className="title">{t('instancePhasePreview.severity')}</span>{ip.status === ModuleInstanceStatus.Started ? t(`moduleInstancePhaseStatus.${ip.status}`) : t(`moduleInstancePhaseSeverity.${ip.severity}`)}</span>
                        <span><span className="title">{t('instancePhasePreview.duration')}</span> {InstancePhaseStatus.getDuration(ip.startedAt, ip.modifiedAt)}</span>
                    </Tooltip>

                </div>
            ))}
        </div>
            {phases.length > 1 &&
                <p style={{ fontSize: '14px', marginTop: '1rem' }}><Link to={`/module/${props.moduleId}/instance/${props.moduleInstanceId}/phaseStatistics`}>{t('instancePhasePreview.seeFull')}</Link></p>
            }
        </>
    }

    const renderBody = () => {
        if (!props.moduleInstanceId) {
            return t('instancePhasePreview.nothingYet') ;
        } else {
            switch (instancePhases.code) {
                case StatusCode.PENDING:
                    return <Loading />;
                case StatusCode.ERROR:
                    return t('instancePhasePreview.failedToFetch');
                case StatusCode.COMPLETE:
                    if (instancePhases.data.length || !props.completed) {
                        return renderPhases(instancePhases.data);
                    } else {
                        return t('instancePhasePreview.nothingYet');
                    }
                default: return;
            }
        }
    };

    return (
        <div className="mt-4">
            <h5>{t('instancePhasePreview.heading')}</h5>
            {renderBody()}
        </div>);
}

export default InstancePhase;
